import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem, Badge } from 'reactstrap';

//constants
import { layoutModeTypes, leftSidebarTypes } from "../../Components/constants/layout";
import { changeSidebarTheme } from "../../slices/thunks";
// Round PREF-1 — the default theme is saved to the per-user Preferences API
// (key "theme") so it follows the user across browsers/machines; localStorage
// remains the instant-boot cache (the layouts reducer reads it for the very
// first paint, and syncPrefsAtStartup refreshes it from the server).
import { savePref } from "../../helpers/prefs_api";

// Round THEME-1b (2026) — full THEME PICKER with per-user default.
//
// Owner requests, in order: better dark mode → 2-3 dark variants →
// "more light theme please also … right now we have only one … also make
// something button [to] make it default any theme by user".
//
// Six themes (3 light + 3 dark):
//   Light · Classic  — the original light theme, untouched
//   Light · Soft     — gentle gray-blue page, less stark white
//   Light · Sand     — warm ivory page
//   Dark  · Midnight — deep navy & violet (reference-site look)
//   Dark  · Graphite — charcoal & teal
//   Dark  · Classic  — original template dark (with the sidebar fixes)
//
// UX: clicking a theme applies it INSTANTLY (live preview). "Set current
// as my default" saves it (localStorage "app.theme") — the saved theme
// shows a "Default" badge and is re-applied on every app load, same
// pattern as the Org Chart default-view control the owner liked.
//
// Mechanics: light/dark still flows through the existing Redux layout
// pipeline (data-layout-mode), so all template behaviors keep working;
// the FLAVOR is a data-app-theme attribute on <html> that
// appDarkThemes.css keys on (dark flavors under [data-layout-mode="dark"],
// light flavors under [data-layout-mode="light"]).
const THEME_KEY = "app.theme";
const LEGACY_DARK_KEY = "app.darkTheme"; // migrated from Round THEME-1

const THEMES = [
    { key: "light-classic", mode: "light", variant: null,       label: "Light · Classic",  icon: "ri-sun-line",         hint: "Original light theme" },
    { key: "light-soft",    mode: "light", variant: "soft",     label: "Light · Soft",     icon: "ri-sun-foggy-line",   hint: "Gentle gray-blue" },
    { key: "light-sand",    mode: "light", variant: "sand",     label: "Light · Sand",     icon: "ri-sun-cloudy-line",  hint: "Warm ivory" },
    { key: "dark-midnight", mode: "dark",  variant: "midnight", label: "Dark · Midnight",  icon: "ri-moon-clear-fill",  hint: "Deep navy & violet" },
    { key: "dark-graphite", mode: "dark",  variant: "graphite", label: "Dark · Graphite",  icon: "ri-contrast-2-line",  hint: "Charcoal & teal" },
    { key: "dark-classic",  mode: "dark",  variant: null,       label: "Dark · Classic",   icon: "ri-moon-line",        hint: "Original template dark" },
];

const readSavedTheme = () => {
    try {
        const v = window.localStorage.getItem(THEME_KEY);
        if (v && THEMES.some((t) => t.key === v)) return v;
        // Migrate the Round THEME-1 dark-flavor key, if present.
        const legacy = window.localStorage.getItem(LEGACY_DARK_KEY);
        if (legacy && THEMES.some((t) => t.key === `dark-${legacy}`)) return `dark-${legacy}`;
    } catch (_) { /* ignore */ }
    return "light-classic";
};

const setHtmlVariant = (variant) => {
    if (variant) document.documentElement.setAttribute("data-app-theme", variant);
    else document.documentElement.removeAttribute("data-app-theme");
};

const LightDark = ({ layoutMode, onChangeLayoutMode }) => {
    const dispatch = useDispatch();
    const isDark = layoutMode === layoutModeTypes['DARKMODE'];

    const [savedKey, setSavedKey] = useState(readSavedTheme);
    // The theme currently APPLIED (live preview may differ from saved).
    const [activeKey, setActiveKey] = useState(readSavedTheme);

    const applyTheme = (theme) => {
        setHtmlVariant(theme.variant);
        const wantDark = theme.mode === "dark";
        onChangeLayoutMode(wantDark ? layoutModeTypes['DARKMODE'] : layoutModeTypes['LIGHTMODE']);
        dispatch(changeSidebarTheme(wantDark ? leftSidebarTypes.DARK : leftSidebarTypes.LIGHT));
        setActiveKey(theme.key);
    };

    // On first mount: apply the user's saved default (covers reloads).
    useEffect(() => {
        const theme = THEMES.find((t) => t.key === readSavedTheme());
        if (theme) applyTheme(theme);
        // Round PREF-1 — when the server prefs arrive (they can land after
        // mount), the server's saved theme wins: re-apply it so the user's
        // cross-machine default takes effect even on a fresh browser.
        const onPrefs = (e) => {
            const serverTheme = e?.detail?.theme;
            if (!serverTheme) return;
            const t = THEMES.find((x) => x.key === serverTheme);
            if (t) { applyTheme(t); setSavedKey(t.key); }
        };
        window.addEventListener("ssw:prefsSynced", onPrefs);
        return () => window.removeEventListener("ssw:prefsSynced", onPrefs);
        
    }, []);

    // If something else flips light/dark (e.g. the RightSidebar settings
    // panel), keep the variant attribute coherent with the new mode.
    useEffect(() => {
        const active = THEMES.find((t) => t.key === activeKey);
        if (!active) return;
        if (isDark && active.mode !== "dark") setHtmlVariant(null);
        if (!isDark && active.mode !== "light") setHtmlVariant(null);
        
    }, [isDark]);

    const saveAsDefault = () => {
        try { window.localStorage.setItem(THEME_KEY, activeKey); } catch (_) { /* ignore */ }
        savePref("theme", activeKey); // Round PREF-1 — roams across machines
        setSavedKey(activeKey);
    };

    const renderItem = (t) => (
        <DropdownItem key={t.key} onClick={() => applyTheme(t)} className="d-flex align-items-center gap-2">
            <i className={`${t.icon} fs-16`}></i>
            <span className="flex-grow-1">
                {t.label}
                <small className="d-block text-muted">{t.hint}</small>
            </span>
            {savedKey === t.key && <Badge color="light" className="border text-muted">Default</Badge>}
            {activeKey === t.key && <i className="ri-check-line text-success"></i>}
        </DropdownItem>
    );

    return (
        <UncontrolledDropdown className="ms-1 header-item d-none d-sm-flex">
            <DropdownToggle
                type="button"
                tag="button"
                className="btn btn-icon btn-topbar btn-ghost-secondary rounded-circle light-dark-mode shadow-none"
                title="Theme"
            >
                <i className={isDark ? 'bx bx-moon fs-22' : 'bx bx-sun fs-22'}></i>
            </DropdownToggle>
            <DropdownMenu end style={{ minWidth: 250 }}>
                <li className="dropdown-header text-uppercase" style={{ fontSize: 11, letterSpacing: '.06em' }}>Light themes</li>
                {THEMES.filter((t) => t.mode === "light").map(renderItem)}
                <DropdownItem divider />
                <li className="dropdown-header text-uppercase" style={{ fontSize: 11, letterSpacing: '.06em' }}>Dark themes</li>
                {THEMES.filter((t) => t.mode === "dark").map(renderItem)}
                <DropdownItem divider />
                <DropdownItem
                    onClick={saveAsDefault}
                    disabled={savedKey === activeKey}
                    className="d-flex align-items-center gap-2"
                    title="The default theme is applied automatically every time you open the dashboard"
                >
                    <i className={savedKey === activeKey ? "ri-star-fill text-warning fs-16" : "ri-star-line fs-16"}></i>
                    <span>{savedKey === activeKey ? "This is your default" : "Set current as my default"}</span>
                </DropdownItem>
            </DropdownMenu>
        </UncontrolledDropdown>
    );
};

export default LightDark;
